/**
 * Category packs: the picker's artwork, one request per category (KPR-1188,
 * KPE-037 "icons on demand").
 *
 * WHY. KPR-1187 serves one small file per icon (`/icons/<NAME>.<hash>.json`),
 * the right unit for a badge that draws one icon. The icon picker draws a page
 * of ~50 at once, and one request per tile is 50 requests. So the build also
 * writes one PACK per picker category, `/icons/packs/<id>.<hash>.json`, holding
 * every icon of that category, and the picker asks for the pack of the page on
 * screen: paging through a category costs one request, and a page seen before
 * costs none.
 *
 * WHAT A PACK HOLDS. `{ "v": 1, "icons": { "<NAME>": { attr, child } } }`: each
 * member is a per-icon artwork file without its own `v` (src/config/iconArtwork.js
 * is the format and the guard; the reader puts `v` back and runs checkArtwork on
 * every member). A pack is built from the per-icon files, never from the
 * registry, so it does not care which source (ICON_SOURCES) drew an icon.
 *
 * ITS HASH is a digest of the pack's format version and its members' names and
 * per-icon hashes (src/config/iconFiles.json), not of the pack's bytes. The
 * bytes are a function of exactly those, so a URL changes exactly when a member
 * does, and a test can check the committed id -> hash map
 * (src/config/iconPacks.json) from the committed icon map alone. A category
 * with a member added, removed or redrawn gets a new hash; `npm run build:icons`
 * writes the map, and the build and iconPacks.test.js fail while it is stale.
 *
 * PURE: no imports. The generator (Node) and jest both call it.
 */

/** The pack file format's version; the reader refuses any other. */
export const PACK_VERSION = 1;

/** Where packs are served, under /icons/ (firebase.json caches /icons/** for a year). */
export const PACKS_DIR = 'packs';

/** Hex characters of the digest that name a pack file. */
export const PACK_HASH_LENGTH = 8;

const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

/** 'People & family' -> 'people-family'. */
export function packIdOfCategory(category) {
	return `${category || ''}`.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/**
 * The packs of a set of icon names: `{ [id]: { category, names } }`, names
 * sorted, ids in the order categories are first met in the sorted names.
 * `categoryOf(name)` is the icon's picker category (the caller supplies the
 * 'Other' fallback).
 */
export function planPacks(names, categoryOf) {
	const plan = {};
	for (const name of [...names].sort()) {
		const category = categoryOf(name);
		const id = packIdOfCategory(category);
		if (!id) throw new Error(`${name}: the category "${category}" makes no pack id`);
		if (!own(plan, id)) plan[id] = { category, names: [] };
		else if (plan[id].category !== category) throw new Error(`the categories "${plan[id].category}" and "${category}" both make the pack id "${id}"`);
		plan[id].names.push(name);
	}
	return plan;
}

/**
 * The pack's hash from its members' per-icon hashes: `members` is
 * `{ NAME: hash }` for exactly the pack's names, `digest(text)` a hex digest
 * (SHA-256 in the generator and the test).
 */
export function packHash(id, members, digest) {
	const text = `pack v${PACK_VERSION} ${id}\n${Object.keys(members).sort().map(name => `${name} ${members[name]}`).join('\n')}\n`;
	return digest(text).slice(0, PACK_HASH_LENGTH);
}

/** The file a pack is published as, under public/icons/packs/. */
export function packFileName(id, hash) {
	return `${id}.${hash}.json`;
}

/**
 * A pack file's bytes. `artworks` is `{ NAME: artwork }` (each with its `v`,
 * which the pack drops): compact JSON, names sorted, a trailing newline.
 */
export function renderPackFile(artworks) {
	const icons = {};
	for (const name of Object.keys(artworks).sort()) {
		const { v, ...rest } = artworks[name]; // eslint-disable-line no-unused-vars
		icons[name] = rest;
	}
	return `${JSON.stringify({ v: PACK_VERSION, icons })}\n`;
}

/**
 * The committed id -> hash map (src/config/iconPacks.json): one pack per line,
 * sorted by id, tabs, a trailing newline.
 */
export function renderIconPacks(hashes) {
	const ids = Object.keys(hashes).sort();
	if (!ids.length) return '{}\n';
	return `{\n${ids.map(id => `\t${JSON.stringify(id)}: ${JSON.stringify(hashes[id])}`).join(',\n')}\n}\n`;
}

/** Null when `pack` has the pack file's shape, else a sentence saying what is wrong. Members are checked one by one by the reader. */
export function checkPack(pack) {
	if (!pack || typeof pack !== 'object' || Array.isArray(pack)) return 'not a pack object';
	if (pack.v !== PACK_VERSION) return `version ${pack.v} is not ${PACK_VERSION}`;
	if (!pack.icons || typeof pack.icons !== 'object' || Array.isArray(pack.icons)) return 'icons is not an object';
	return null;
}
